/**
 * What a supervisor weighs before approving a change to someone's schedule
 * beyond what availability says (docs/product/scheduling-model.md's "Open
 * shifts" and "Shift swaps"): whether it would put the agent over their tour of
 * duty for a pay period, and whether they hold the qualification the assignment
 * asks for. Pure. Both are information for the supervisor and neither blocks an
 * approval.
 */

export const DEFAULT_TOUR_OF_DUTY_HOURS = 80

const PERIOD_ANCHOR = Date.UTC(2026, 8, 8)
const PERIOD_LENGTH_DAYS = 14

// The first day of the 14-day pay period holding a date, from the same anchor
// as muster.pay_period_start.
export function periodStartOf(date: string): string {
  const [year, month, day] = date.split('-').map(Number)
  const diff = Math.floor((Date.UTC(year, month - 1, day) - PERIOD_ANCHOR) / 86400000)
  return new Date(PERIOD_ANCHOR + Math.floor(diff / PERIOD_LENGTH_DAYS) * PERIOD_LENGTH_DAYS * 86400000).toISOString().slice(0, 10)
}

// The hours a timed shift schedules, after its meal period. A shift with no
// times schedules none.
export function shiftHours(startTime: string | null | undefined, endTime: string | null | undefined, mealMinutes = 0): number {
  if (!startTime || !endTime) return 0
  const minutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5))
  const span = minutes(endTime) - minutes(startTime)
  // An end before the start is the next morning.
  return Math.max(0, ((span < 0 ? span + 24 * 60 : span) - mealMinutes) / 60)
}

const hoursText = (hours: number) => `${Number(hours.toFixed(2))}`

export type ScheduledDay = { date: string; hours: number }

// A change to the hours scheduled on a date: positive for a shift taken on,
// negative for one given up or replaced.
export type HoursChange = { date: string; delta: number }

/**
 * A warning for each pay period the changes touch where the agent would end up
 * with more hours than their tour of duty and a change adds to it. `subject`
 * names the agent where a warning could be about either of two people.
 */
export function overTourWarnings(params: {
  scheduled: ScheduledDay[]
  changes: HoursChange[]
  tourHours: number
  subject?: string
}): string[] {
  const { scheduled, changes, tourHours, subject } = params
  const periods = Array.from(new Set(changes.map((change) => periodStartOf(change.date)))).sort()
  return periods.flatMap((period) => {
    const inPeriod = changes.filter((change) => periodStartOf(change.date) === period)
    if (!inPeriod.some((change) => change.delta > 0)) return []
    const current = scheduled.filter((day) => periodStartOf(day.date) === period).reduce((sum, day) => sum + day.hours, 0)
    const projected = current + inPeriod.reduce((sum, change) => sum + change.delta, 0)
    if (projected <= tourHours + 1e-9) return []
    return [`${subject ? `${subject} would` : 'Would'} schedule ${hoursText(projected)} hours in the pay period, over the ${hoursText(tourHours)}-hour tour of duty.`]
  })
}

export type HeldQualification = { qualificationId: string; effectiveFrom: string; effectiveTo?: string }

/**
 * A warning when an assignment asks for a qualification and the agent does not
 * hold it on the date, or null. An assignment that asks for none, or none
 * given, never warns.
 */
export function missingQualificationWarning(params: {
  required?: { qualificationId: string; name: string }
  held: HeldQualification[]
  date: string
  assignmentName?: string
  subject?: string
}): string | null {
  const { required, held, date, assignmentName, subject } = params
  if (!required) return null
  const holds = held.some(
    (item) => item.qualificationId === required.qualificationId && item.effectiveFrom <= date && (!item.effectiveTo || item.effectiveTo >= date),
  )
  if (holds) return null
  return `${subject ? `${subject} does` : 'Does'} not hold the ${required.name} qualification${assignmentName ? ` that ${assignmentName} asks for` : ' the assignment asks for'}.`
}
